import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import PaymentForm from './PaymentForm';
import { PaymentSuccessModalContent } from './PaymentSuccess';
import { secureStorage } from '../Utils/encryption';
import { getCurrencies, postApi, getApi } from '../services/apiServices';
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { getStripePublishableKey } from '../services/stripeService';
import WalletButton from './walletButton';
import GooglePayWebButton from './GooglePayWebButton';
import '../styles/r4l-tokens.css';
import '../styles/r4l-checkout.css';
const stripePromise = loadStripe(getStripePublishableKey()).catch(error => {
  console.error('Failed to load Stripe:', error);
  return null;
});

// Apple Pay: Stripe paymentRequest() with disableWallets: ['googlePay'] (Safari). Google Pay: pay.js only (Chrome/Android), token to backend. Card: Stripe Elements.

const parseNumericString = (str) => {
  if (!str) return 0;

  return parseFloat(str.toString().replace(/,/g, '')) || 0;
};

// Format travel dates like "15 Dec - 30 Dec" (adds year if different)
const formatDateRange = (start, end, locale = 'en-US') => {
  if (!start) return '';

  const startDate = new Date(start);
  const endDate = end ? new Date(end) : null;
  const isValid = (date) => date instanceof Date && !isNaN(date);

  if (!isValid(startDate)) return '';

  const baseOpts = { day: 'numeric', month: 'short' };
  const format = (date, opts = baseOpts) => new Intl.DateTimeFormat(locale || 'en-US', opts).format(date);

  if (endDate && isValid(endDate)) {
    const opts = startDate.getFullYear() !== endDate.getFullYear()
      ? { ...baseOpts, year: 'numeric' }
      : baseOpts;
    return `${format(startDate, opts)} - ${format(endDate, opts)}`;
  }

  return format(startDate);
};

const isCartItemExpired = (item) => {
 
  if (item.plan_type !== 'Unlimited Plans' || !item.startDate) {
    return false;
  }

  const startDate = new Date(item.startDate);
  // Normalize startDate to end of day (23:59:59.999) - item expires after start date has passed
  startDate.setHours(23, 59, 59, 999);

  const today = new Date();
  // Normalize today to start of day (00:00:00.000) for consistent comparison
  today.setHours(0, 0, 0, 0); 

  // Item is expired if today is after the start date
  // Example: if startDate is Nov 27, item expires on Nov 28 (day after start date)
  return today > startDate;
};

const getExpiredCartItems = (cartItems) => {
  return cartItems.filter(item => isCartItemExpired(item));
};

const removeExpiredItems = (cartItems) => {
  return cartItems.filter(item => !isCartItemExpired(item));
};

/** Simple Safari detection: hide Google Pay on Safari (use Apple Pay there). */
const isSafariBrowser = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const vendor = navigator.vendor || '';
  return vendor === 'Apple Computer, Inc.' || (/Safari/i.test(ua) && !/Chrome|CriOS|FxiOS/i.test(ua));
};

// Read the FirstPromoter tracking ID cookie (_fprom_tid) set by fpr.js
// This is the unique click ID generated when a visitor arrives via an affiliate link.
// It is required by the FirstPromoter track/sale API as the `tid` parameter.
const getAffiliateTid = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [key, value] = cookie.trim().split('=');
    // _fprom_tid is the correct tracking ID cookie (set by fpr.js on affiliate link click)
    if (key === '_fprom_tid') {
      return decodeURIComponent(value || '');
    }
  }
  return '';
};

function Checkout() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const stripeLocale = (currentLanguage || 'en').split('-')[0];
  const [cartItems, setCartItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    return localStorage.getItem('selectedCurrency') || 'USD';
  });
  const abortControllerRef = useRef(null);
  const [currencies, setCurrencies] = useState([]);
  const [apiCalculatedTotal, setApiCalculatedTotal] = useState(0);
  const [isCalculatingTotal, setIsCalculatingTotal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [stripeError, setStripeError] = useState(null);
  const [expiredItems, setExpiredItems] = useState([]);
  const [hasExpiredItems, setHasExpiredItems] = useState(false);
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [checkoutName, setCheckoutName] = useState('');
  const [checkoutEmail, setCheckoutEmail] = useState('');
  const [checkoutPhone, setCheckoutPhone] = useState('');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  // FirstPromoter affiliate tracking token (read from cookie set by fpr.highlevel.js)
  const [affiliateTid] = useState(() => getAffiliateTid());
  const navigate = useNavigate();

  const isSafari = useMemo(() => isSafariBrowser(), []);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handleChange = (e) => setIsMobile(e.matches);
    handleChange(mql);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // Language mapping function to convert app language codes to API language codes
  const getApiLanguageCode = (appLanguage) => {
    const languageMap = {
      'ar': 'ar',     // Arabic
      'da': 'da',     // Danish
      'de': 'de',     // German
      'en': 'en',     // English
      'es': 'es',     // Spanish
      'fr': 'fr',     // French
      'he': 'he',     // Hebrew
      'it': 'it',     // Italian
      'ja': 'ja',     // Japanese
      'ko': 'ko',     // Korean
      'nl': 'nl',     // Dutch
      'no': 'no',     // Norwegian
      'pl': 'pl',     // Polish
      'pt': 'pt',     // Portuguese
      'ru': 'ru',     // Russian
      'sv': 'sv',     // Swedish
      'tr': 'tr',     // Turkish
      'zh': 'zh_CN'   // Chinese (Simplified)
    };
    return languageMap[appLanguage] || 'en'; // Default to English if not found
  };

  useEffect(() => {
    const storedCurrency = localStorage.getItem('selectedCurrency');
    const initialCurrency = storedCurrency || selectedCurrency;
    if (storedCurrency) {
      setSelectedCurrency(storedCurrency);
    }

    const loadCartItems = (currency = initialCurrency) => {
      const savedItems = secureStorage.getItem('checkoutSession') || [];

      // Check for expired items
      const expiredItemsList = getExpiredCartItems(savedItems);
      const validItems = removeExpiredItems(savedItems);

      if (expiredItemsList.length > 0) {
        setExpiredItems(expiredItemsList);
        setHasExpiredItems(true);

        // Update cart with only valid items
        setCartItems(validItems);
        secureStorage.setItem('checkoutSession', validItems);

        // Show notification about expired items
        toast.error(
          `${expiredItemsList.length} item(s) in your cart have expired and have been removed. Please add new items to continue.`,
          { autoClose: 5000 }
        );
      } else {
        setExpiredItems([]);
        setHasExpiredItems(false);
        setCartItems(savedItems);
      }

      if (validItems.length > 0) {
        calculateCartTotal(validItems, currency);
      } else {
        setApiCalculatedTotal(0);
        setTotalPrice(0);
      }

      setIsLoading(false);
    };


    loadCartItems(initialCurrency);

    const handleCartUpdated = () => {
      const latestCurrency = localStorage.getItem('selectedCurrency') || initialCurrency;
      setSelectedCurrency(latestCurrency);
      loadCartItems(latestCurrency);
    };

    window.addEventListener('cartUpdated', handleCartUpdated);
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdated);
    };
  }, []);

  useEffect(() => {
    if (cartItems.length > 0) {
      calculateCartTotal(cartItems, selectedCurrency);
    }
  }, [selectedCurrency]);



  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const apiLanguage = getApiLanguageCode(currentLanguage);
        const response = await getApi(`/api/currencies?lang=${apiLanguage}`);
        if (response?.status && Array.isArray(response.data)) {
          setCurrencies(response.data);
        }
      } catch (err) {
        // Silently handle currency fetch errors
      }
    };
    fetchCurrencies();
  }, [currentLanguage]);

  // Stripe load check
  useEffect(() => {
    stripePromise.then((stripe) => {
      if (!stripe) setStripeError('Stripe failed to load');
    }).catch(() => setStripeError('Stripe failed to load'));
  }, []);

  // Apple Pay only: stripe.paymentRequest with disableWallets: ['googlePay'] so Stripe does NOT trigger Google Pay (we use pay.js for Google Pay).
  useEffect(() => {
    stripePromise.then(async (stripe) => {
      if (!stripe || totalPrice <= 0) {
        setApplePayAvailable(false);
        return;
      }
      try {
        const isUsingHttps = window.location.protocol === 'https:';
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (!isUsingHttps && !isLocalhost) {
          setApplePayAvailable(false);
          return;
        }
        const pr = stripe.paymentRequest({
          country: 'US',
          currency: selectedCurrency.toLowerCase(),
          total: { label: 'Apple Pay', amount: Math.round(totalPrice * 100) },
          requestPayerName: true,
          requestPayerEmail: true,
          disableWallets: ['googlePay'],
        });
        const result = await pr.canMakePayment().catch(() => null);
        setApplePayAvailable(Boolean(result && result.applePay));
      } catch {
        setApplePayAvailable(false);
      }
    }).catch(() => setApplePayAvailable(false));
  }, [totalPrice, selectedCurrency]);

  const calculateCartTotal = async (items = cartItems, currency = selectedCurrency) => {
    if (items.length === 0) {
      setApiCalculatedTotal(0);
      setTotalPrice(0);
      return;
    }

    // Abort previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsCalculatingTotal(true);
    try {
      const apiItems = items.map(item => ({
        type: item.plan_type === 'Data Bundles' ? 'bundle' :
          item.plan_type === 'Global eSIM' ? 'global' : 'unlimited',
        country: item.country_code || item.iso3 || 'USA',
        days: item.days || 1,
        no_of_esims: item.esim_count || 1,
        data_usage_allowance: item.gb || 20
      }));

      const apiLanguage = getApiLanguageCode(currentLanguage);
      const payload = {
        items: apiItems,
        currency: currency,
        lang: apiLanguage
      };

      const response = await postApi('/api/calculate-cart', payload, { signal: controller.signal });

      if (response?.status && response.data?.total) {
        const total = parseNumericString(response.data.total);
        setApiCalculatedTotal(total);
        setTotalPrice(total);

        if (response.data.items && response.data.items.length > 0) {
          const updatedItems = [...items];
          response.data.items.forEach((apiItem, index) => {
            if (updatedItems[index]) {
              updatedItems[index].api_calculated_price = parseNumericString(apiItem.amount);
            }
          });
          setCartItems(updatedItems);
          secureStorage.setItem('checkoutSession', updatedItems);
        }
      } else {
        const fallbackTotal = getFallbackTotalPrice(items);
        setApiCalculatedTotal(fallbackTotal);
        setTotalPrice(fallbackTotal);
      }
    } catch (error) {
      // Check if this is an abort error
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED' || error?.code === 20) {
        return;
      }
      const fallbackTotal = getFallbackTotalPrice(items);
      setApiCalculatedTotal(fallbackTotal);
      setTotalPrice(fallbackTotal);
    } finally {
      // Only set loading to false if this was the last request
      if (abortControllerRef.current === controller) {
        setIsCalculatingTotal(false);
      }
    }
  };

  const getFallbackTotalPrice = (items) => {
    const total = items.reduce((sum, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      return sum + itemPrice;
    }, 0);
    return Math.round(total * 100) / 100;
  };

  const getTotalPrice = () => {
    return apiCalculatedTotal;
  };

  const handlePaymentSuccess = (paymentIntent) => {
    setIsPaymentSuccessful(true);
    secureStorage.removeItem('checkoutSession');
    setCartItems([]);
    setTotalPrice(0);
    setApiCalculatedTotal(0);
    window.dispatchEvent(new Event('cartUpdated'));
    setShowSuccessModal(true);
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    navigate('/');
  };

  const handleSuccessInstallEsim = () => {
    setShowSuccessModal(false);
    navigate('/activateEsim');
  };

  const handlePaymentError = (error) => {
  
    const errorMessage = error.message || t('checkout.paymentError');
    if(error?.code==20)return;
    toast.error(errorMessage);
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
  };

  const removeFromCart = async (index) => {
    const updatedItems = cartItems.filter((_, i) => i !== index);
    setCartItems(updatedItems);
    secureStorage.setItem('checkoutSession', updatedItems);
    await calculateCartTotal(updatedItems, selectedCurrency);
  };

  // Validate cart items before payment
  const validateCartItems = () => {
    const expiredItemsList = getExpiredCartItems(cartItems);
    if (expiredItemsList.length > 0) {
      const expiredItemNames = expiredItemsList.map(item => `${item.country} (${item.plan_type === 'Global eSIM' ? t('destinationDetail.tabs.globalESIM') : item.plan_type})`).join(', ');
      toast.error(
        `Cannot proceed with payment. The following item(s) have expired: ${expiredItemNames}. Please refresh the page to remove expired items.`,
        { autoClose: 8000 }
      );
      return false;
    }
    return true;
  };


  if (isLoading) {
    return (
      <section className="destinations checkout">
        <div className="container">
          <div className="row">
            <div className="col-sm-12">
              <div className="checkout-left text-center">
                <div className="logo">
                  <Link to="/">
                    <img src="images/logo.svg" alt="Logo" />
                  </Link>
                </div>
                <div className="loading">
                  <h3>{t('checkout.loading')}</h3>
                  <div className="spinner"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Check if Stripe failed to load
  if (stripeError) {
    return (
      <section className="destinations checkout">
        <div className="container">
          <div className="row">
            <div className="col-sm-12">
              <div className="checkout-left text-center">
                <div className="logo">
                  <Link to="/">
                    <img src="images/logo.svg" alt="Logo" />
                  </Link>
                </div>
                <div className="error-message">
                  <h3>Payment System Unavailable</h3>
                  <p>Stripe failed to load. This usually means <strong>REACT_APP_STRIPE_PUBLISHABLE_KEY</strong> is missing or invalid in your environment.</p>
                  <p className="small text-muted mb-0">Fix: Add a valid <code>pk_test_</code> or <code>pk_live_</code> key to <code>.env</code>, then rebuild the app (<code>npm run build</code> or restart <code>npm start</code>).</p>
                  <Link to="/" className="view-all get-sim mt-3">
                    Return to Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (cartItems.length === 0 && !isPaymentSuccessful) {
    return (
      <section className="destinations checkout">
        <div className="container">
          <div className="row">
            <div className="col-sm-12">
              <div className="checkout-left">
                <div className="logo">
                  <Link to="/">
                    <img src="images/logo.svg" alt="Logo" />
                  </Link>
                </div>
                <div className="empty-cart">
                  <span><img alt="Cart" src="/images/cart.svg" /></span>
                  <h3>{t('checkout.emptyCart.title')}</h3>
                  <p>{t('checkout.emptyCart.description')}</p>
                  <Link to="/" className="view-all get-sim">
                    {t('checkout.emptyCart.continueShopping')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }



  return (
    <>
    <section className="destinations checkout checkout-bg r4l-checkout">
      <div className="container">
        <div className="row">
          <div className="col-sm-6">
            <div className="checkout-left">
              <div className="logo">
                <Link to="/">
                  <img src={window.getPublicUrl('images/logo.png')} alt="Roam4Less" />
                </Link>
              </div>

              {/* Stripe defaults to the browser language; pass the site language so the card field matches the page.
                  key remounts Elements when the language changes, since locale can't be updated in place. */}
              <Elements stripe={stripePromise} options={{ locale: stripeLocale }} key={stripeLocale}>
                {/* Apple Pay (Stripe, disableWallets: googlePay) + Google Pay (pay.js only). No Stripe Payment Request for Google Pay. */}
                {(totalPrice > 0 || applePayAvailable) && (
                  <div className="choose-payment mb-2">
                    <h3 className='payment-headimg'>{t('checkout.choosePaymentMethod')}</h3>
                    <p className="text-muted mb-2" style={{ fontSize: '17px', marginTop: '2px' }}>Express checkout</p>
                    {totalPrice > 0 && !isMobile && (
                      <>
                        <div className="mb-3">
                          <label htmlFor="checkout-name" className="form-label">
                            {t('paymentForm.name') || 'Full Name'} *
                          </label>
                          <input
                            id="checkout-name"
                            type="text"
                            className="form-control"
                            placeholder={t('paymentForm.namePlaceholder') || 'Your full name'}
                            value={checkoutName}
                            onChange={(e) => setCheckoutName(e.target.value)}
                            required
                          />
                        </div>
                        <div className="mb-3">
                          <label htmlFor="checkout-email" className="form-label">
                            {t('paymentForm.email') || 'Email'} *
                          </label>
                          <input
                            id="checkout-email"
                            type="email"
                            className="form-control"
                            placeholder={t('paymentForm.emailPlaceholder') || 'your@email.com'}
                            value={checkoutEmail}
                            onChange={(e) => setCheckoutEmail(e.target.value)}
                            required
                          />
                          {/* <small className="text-muted">{t('checkout.nameEmailRequiredForGooglePay') || 'Name and email required for Google Pay'}</small> */}
                        </div>
                        <div className="mb-3">
                          <label htmlFor="checkout-phone" className="form-label">
                            {t('paymentForm.phone')}
                          </label>
                          <input
                            id="checkout-phone"
                            type="tel"
                            autoComplete="tel"
                            className="form-control"
                            placeholder={t('paymentForm.phonePlaceholder')}
                            value={checkoutPhone}
                            onChange={(e) => setCheckoutPhone(e.target.value)}
                          />
                        </div>
                      </>
                    )}
                    <div
                      className={`payment-methods${[applePayAvailable, totalPrice > 0].filter(Boolean).length === 1 ? ' payment-methods--single' : ''}`}
                    >
                      {applePayAvailable && (
                        <WalletButton
                          walletType="apple"
                          totalAmount={totalPrice}
                          cartItems={cartItems}
                          selectedCurrency={selectedCurrency}
                          country="US"
                          onPaymentSuccess={handlePaymentSuccess}
                          onPaymentError={handlePaymentError}
                          validateCartItems={validateCartItems}
                          affiliateTid={affiliateTid}
                        />
                      )}
                      {totalPrice > 0 && (
                      // {totalPrice > 0 && !isSafari && (
                        <GooglePayWebButton
                          totalAmount={totalPrice}
                          cartItems={cartItems}
                          selectedCurrency={selectedCurrency}
                          country="US"
                          merchantName="Roam4Less"
                          customerName={checkoutName}
                          customerEmail={checkoutEmail}
                          customerPhone={checkoutPhone}
                          onPaymentSuccess={handlePaymentSuccess}
                          onPaymentError={handlePaymentError}
                          validateCartItems={validateCartItems}
                          showBillingModalWhenEmpty={isMobile}
                          affiliateTid={affiliateTid}
                        />
                      )}
                    </div>
                  </div>
                )}

                {(totalPrice > 0 || applePayAvailable) && (
                  <div className='or-options mb-3'>
                    <hr className='left'></hr>{t('checkout.or')}<hr className='right'></hr>
                  </div>
                )}

                {/* Card Payment */}
                <PaymentForm
                  totalAmount={totalPrice}
                  cartItems={cartItems}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  selectedCurrency={selectedCurrency}
                  validateCartItems={validateCartItems}
                  affiliateTid={affiliateTid}
                />
              </Elements>
            </div>
          </div>

          <div className="col-sm-6">
            <div className="checkout-left right">
              <div className="choose-payment">
                <div className='d-flex justify-content-between mb-3 align-items-center'>
                  <h3>{t('checkout.orderSummary')}</h3>
                </div>
                <div className='payment-cart-scroll'>
                  {cartItems.map((item, index) => {
                    const isExpired = isCartItemExpired(item);
                    return (
                      <div key={index} className={`popular-box ${isExpired ? 'expired-item' : ''}`}>
                        <img
                          className='close-btn'
                          src={window.getPublicUrl('images/close.svg')}
                          alt="Close"
                          onClick={() => removeFromCart(index)}
                          style={{ cursor: 'pointer' }}
                        />

                        <span>
                          <img
                            src={item.flag || window.getPublicUrl('images/earth-globe-global-svgrepo-com (1).svg')}
                            alt={`${item.country} eSIM`}
                            onError={(e) => {
                              e.target.src = window.getPublicUrl('images/earth-globe-global-svgrepo-com (1).svg');
                            }}
                          />
                        </span>
                        <div className="sim-text">
                          <h4>{t('checkout.eSIM')}</h4>
                          <h3>{item.country}</h3>
                          <h5><strong>{item.plan_type === 'Global eSIM' ? t('destinationDetail.tabs.globalESIM') : item.plan_type}</strong></h5>
                          <h5>{item.days} {t('checkout.days')}</h5>
                          {item.startDate && (
                            <h5>{formatDateRange(item.startDate, item.endDate, currentLanguage)}</h5>
                          )}
                          {item.gb && <h5>{item.gb} {t('checkout.gb')}</h5>}

                          <h5>{t('checkout.quantity')}: {item.esim_count || 1}</h5>
                          {isExpired && (
                            <div className="expired-warning" style={{
                              color: '#dc3545',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              marginTop: '5px'
                            }}>
                              ⚠️ This item has expired
                            </div>
                          )}
                          <h3>
                            {(() => {
                              const currentCurrencyCode = item.currency || selectedCurrency;
                              const currentCurrencyObj = currencies.find(c => c.code === currentCurrencyCode);
                              return currentCurrencyObj?.symbol || '$';
                            })()}{(parseFloat(item.api_calculated_price || item.price) || 0).toFixed(2)} {item.currency || selectedCurrency}
                          </h3>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="plan-info-bottom">
                  <h3>{t('checkout.total')}</h3>
                  <h4>
                    {isCalculatingTotal ? (
                      <span>{t('checkout.calculating')}</span>
                    ) : (
                      <>
                        {(() => {
                          const currentCurrencyCode = cartItems[0]?.currency || selectedCurrency;
                          const currentCurrencyObj = currencies.find(c => c.code === currentCurrencyCode);
                          return currentCurrencyObj?.symbol || '$';
                        })()}{getTotalPrice().toFixed(2)} <strong>{cartItems[0]?.currency || selectedCurrency}</strong>
                      </>
                    )}
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <Modal
      isOpen={showSuccessModal}
      onRequestClose={closeSuccessModal}
      style={{
        content: {
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          marginRight: '-50%',
          transform: 'translate(-50%, -50%)',
          border: 'none',
          borderRadius: '10px',
          padding: '24px',
          maxWidth: '560px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          zIndex: 1001,
        },
        overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000 },
      }}
      contentLabel={t('paymentSuccess.title')}
      portalClassName="checkout-success-modal-portal"
    >
      <PaymentSuccessModalContent
        onInstallEsim={handleSuccessInstallEsim}
        onGoHome={closeSuccessModal}
      />
    </Modal>
    </>
  );
}

export default Checkout;